import { BackgroundSplashImage } from "@Light/components/page/SplashImage";
import { useScaffold } from "@Light/scaffold";
import { useMediaQuery } from "@Light/utils/mediaQuery";
import type { MotionValue } from "motion/react";
import { animate, motion, useMotionValue, useTransform } from "motion/react";
import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { createPortal } from "react-dom";

export type OverlayProps = {
  children: React.ReactNode;
  initialExpanded?: boolean;
};

export type OverlayMaxHeight = {
  collapsed: number;
  expanded: number;
};

export const DEFAULT_OVERLAY_MAX_HEIGHT: OverlayMaxHeight = {
  collapsed: 750,
  expanded: 712,
};

export type OverlayContextState = {
  /**
   * Whether or not the overlay is in expanded state. Updates only after the
   * container reaches its target width.
   */
  isExpanded: boolean;
  /** Whether the viewport is wide enough for the expanded state. */
  hasRoomToExpand: boolean;
  /**
   * Transitions the overlay to or from the expanded state. Takes in a `swap`
   * function to call once the width has been fully transitioned. Calling this
   * is a no-op on small screens or if it's already in the desired state.
   *
   * Use `options.fadeOut: false` to skip the initial fade out and swap immediately.
   */
  transitionExpanded: (
    expanded: boolean,
    swap?: () => void,
    options?: { fadeOut?: boolean },
  ) => Promise<void>;
  /** Opacity children can opt into to fade with the expand/collapse. */
  contentOpacity: MotionValue<number> | null;
  portalContainer: HTMLElement | null;
  /**
   * Override overlay max-height in px. Enrollment keeps
   * {@link DEFAULT_OVERLAY_MAX_HEIGHT} (750 collapsed / 712 expanded).
   */
  setMaxHeight: Dispatch<SetStateAction<OverlayMaxHeight>>;
};

const OverlayContext = createContext<OverlayContextState>({
  isExpanded: false,
  hasRoomToExpand: false,
  transitionExpanded: (_expanded, swap) => {
    swap?.();
    return Promise.resolve();
  },
  contentOpacity: null,
  portalContainer: null,
  setMaxHeight: () => {},
});

export const useOverlay = () => useContext(OverlayContext);

/** Apply flow-specific overlay max-height while mounted; restores defaults on unmount. */
export function useOverlayMaxHeight(maxHeight: Partial<OverlayMaxHeight>) {
  const { setMaxHeight } = useOverlay();
  const collapsed = maxHeight.collapsed;
  const expanded = maxHeight.expanded;

  useLayoutEffect(() => {
    setMaxHeight((prev) => {
      const collapsedNext = collapsed ?? prev.collapsed;
      const expandedNext = expanded ?? prev.expanded;
      if (collapsedNext === prev.collapsed && expandedNext === prev.expanded) {
        return prev;
      }
      return { collapsed: collapsedNext, expanded: expandedNext };
    });
    return () => {
      setMaxHeight(DEFAULT_OVERLAY_MAX_HEIGHT);
    };
  }, [setMaxHeight, collapsed, expanded]);
}

export type OverlayPortalProps = {
  children: React.ReactNode;
};

/**
 * Renders children at the base of the surrounding `Overlay` (if available).
 */
export const OverlayPortal: React.FC<OverlayPortalProps> = ({ children }) => {
  const { portalContainer } = useOverlay();
  if (!portalContainer) {
    return <>{children}</>;
  }
  return createPortal(children, portalContainer);
};

/** Awaits an animation, resolving quietly when it's interrupted by another. */
const settle = (controls: { finished: Promise<unknown> }) =>
  controls.finished.catch(() => undefined);

const COLLAPSED_WIDTH = 400;
const EXPANDED_WIDTH = 840;
const OVERLAY_Y_MARGIN = 24;
const EXPAND_MIN_VIEWPORT = EXPANDED_WIDTH + OVERLAY_Y_MARGIN;
const EXPAND_QUERY = `(min-width: ${EXPAND_MIN_VIEWPORT}px)`;

const viewportHasRoomToExpand = () =>
  typeof window !== "undefined" && window.matchMedia(EXPAND_QUERY).matches;

const FADE = { duration: 0.4, ease: "easeOut" } as const;
const RESIZE = { type: "spring", visualDuration: 0.5, bounce: 0.2 } as const;
const HOLD_MS = 100;

export const Overlay: React.FC<OverlayProps> = ({
  children,
  initialExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const width = useMotionValue(
    initialExpanded && viewportHasRoomToExpand()
      ? EXPANDED_WIDTH
      : COLLAPSED_WIDTH,
  );
  const widthPx = useTransform(width, (value) => `${value}px`);
  const maxHeightConfigRef = useRef({ ...DEFAULT_OVERLAY_MAX_HEIGHT });
  const maxHeight = useMotionValue(
    initialExpanded && viewportHasRoomToExpand()
      ? DEFAULT_OVERLAY_MAX_HEIGHT.expanded
      : DEFAULT_OVERLAY_MAX_HEIGHT.collapsed,
  );
  const maxHeightPx = useTransform(maxHeight, (value) => `${value}px`);
  const contentOpacity = useMotionValue(1);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  const running = useRef(false);
  const transitionCount = useRef(0);

  const setMaxHeight = (action: SetStateAction<OverlayMaxHeight>) => {
    const prev = maxHeightConfigRef.current;
    const next = typeof action === "function" ? action(prev) : action;
    if (next.collapsed === prev.collapsed && next.expanded === prev.expanded) {
      return;
    }
    maxHeightConfigRef.current = next;
    if (!running.current) {
      maxHeight.set(
        width.get() === EXPANDED_WIDTH ? next.expanded : next.collapsed,
      );
    }
  };

  const hasRoomToExpand = useMediaQuery(EXPAND_QUERY, (matches) => {
    if (!running.current) {
      width.set(isExpanded && matches ? EXPANDED_WIDTH : COLLAPSED_WIDTH);
      maxHeight.set(
        isExpanded && matches
          ? maxHeightConfigRef.current.expanded
          : maxHeightConfigRef.current.collapsed,
      );
    }
  });

  const expanded = isExpanded && hasRoomToExpand;

  const scaffold = useScaffold();
  const { splashImage } = scaffold.page;

  const transitionExpanded = async (
    next: boolean,
    swap?: () => void,
    { fadeOut = true }: { fadeOut?: boolean } = {},
  ) => {
    const target = next && hasRoomToExpand ? EXPANDED_WIDTH : COLLAPSED_WIDTH;
    const maxHeightTarget =
      next && hasRoomToExpand
        ? maxHeightConfigRef.current.expanded
        : maxHeightConfigRef.current.collapsed;

    if (!running.current && target === width.get()) {
      maxHeight.set(maxHeightTarget);
      swap?.();
      setIsExpanded(next);
      return;
    }

    // Cancel transition and allow next one to overtake it if a call comes in while mid-transition
    transitionCount.current += 1;
    const thisCount = transitionCount.current;
    const isLatest = () => transitionCount.current === thisCount;

    running.current = true;
    try {
      if (fadeOut) {
        await settle(animate(contentOpacity, 0, FADE));
        if (!isLatest()) return;
        await new Promise((resolve) => setTimeout(resolve, HOLD_MS));
        if (!isLatest()) return;
      } else {
        contentOpacity.jump(0);
      }
      swap?.();
      setIsExpanded(next);
      await Promise.all([
        settle(animate(width, target, RESIZE)),
        settle(animate(maxHeight, maxHeightTarget, RESIZE)),
      ]);
      if (!isLatest()) return;
      await settle(animate(contentOpacity, 1, FADE));
    } finally {
      if (isLatest()) running.current = false;
    }
  };

  const value = {
    isExpanded: expanded,
    hasRoomToExpand,
    transitionExpanded,
    contentOpacity,
    portalContainer,
    setMaxHeight,
  };

  return (
    <OverlayContext.Provider value={value}>
      <div className="relative flex justify-center items-center sm:min-h-screen bg-overlay/85">
        {splashImage && <BackgroundSplashImage />}
        <motion.div
          data-overlay-expanded={expanded ? "" : undefined}
          className="flex flex-col *:w-full w-full mx-auto bg-white sm:rounded-4xl shadow-lg overflow-hidden h-svh md:h-screen sm:w-(--overlay-width,400px) sm:max-w-[calc(100vw-var(--overlay-y-margin))] sm:max-h-[min(var(--overlay-max-height,750px),calc(100vh-var(--overlay-y-margin)))] relative"
          style={{
            ["--overlay-width" as string]: widthPx,
            ["--overlay-max-height" as string]: maxHeightPx,
            ["--overlay-y-margin" as string]: `${OVERLAY_Y_MARGIN}px`,
          }}
        >
          {children}
          <div ref={setPortalContainer} className="contents" />
        </motion.div>
      </div>
    </OverlayContext.Provider>
  );
};
